import { onBeforeUnmount, ref, watch } from "vue";
import { orderRecapService } from "@/services/orderRecapService";
const POLL_MS = 2500;
const POLL_LIMIT_MS = 90_000;
function isClientError(error) {
    const status = error?.response?.status;
    return status !== undefined && status >= 400 && status < 500;
}
/** The cached AI recap for one PA: loads on open and polls while a new one is written. */
export function useOrderRecap(orderKey) {
    const recap = ref(null);
    const unavailable = ref(false);
    const slow = ref(false);
    const rating = ref(false);
    let generation = 0;
    let timer;
    let pollStarted = 0;
    function stopPolling() {
        clearTimeout(timer);
        timer = undefined;
    }
    async function fetchRecap(key, token) {
        try {
            const result = await orderRecapService.get(key);
            if (token !== generation)
                return;
            if (result.status === "disabled") {
                stopPolling();
                recap.value = null;
                unavailable.value = true;
                return;
            }
            const current = recap.value;
            const ratingInFlight = rating.value && current?.version === result.version;
            recap.value = ratingInFlight && current ? { ...result, rating: current.rating, reason: current.reason } : result;
            unavailable.value = false;
            if (result.status === "generating")
                schedulePoll(key, token);
            else {
                stopPolling();
                slow.value = false;
                pollStarted = 0;
            }
        }
        catch (error) {
            if (token !== generation)
                return;
            if (isClientError(error)) {
                stopPolling();
                unavailable.value = true;
            }
            else if (recap.value?.status === "generating")
                schedulePoll(key, token);
            else {
                stopPolling();
                slow.value = !recap.value;
            }
        }
    }
    function schedulePoll(key, token) {
        stopPolling();
        if (!pollStarted)
            pollStarted = Date.now();
        if (Date.now() - pollStarted > POLL_LIMIT_MS) {
            slow.value = true;
            return;
        }
        timer = setTimeout(() => fetchRecap(key, token), POLL_MS);
    }
    function open(key) {
        generation += 1;
        stopPolling();
        recap.value = null;
        unavailable.value = false;
        slow.value = false;
        rating.value = false;
        pollStarted = 0;
        if (key)
            fetchRecap(key, generation);
    }
    async function refresh() {
        const key = orderKey.value;
        if (!key)
            return;
        if (!recap.value) {
            open(key);
            return;
        }
        const token = generation;
        slow.value = false;
        pollStarted = 0;
        if (recap.value)
            recap.value = { ...recap.value, status: "generating" };
        try {
            await orderRecapService.refresh(key);
        }
        catch {
            /* the next poll reports the real state */
        }
        if (token === generation)
            schedulePoll(key, token);
    }
    async function rate(value, reason = "") {
        const key = orderKey.value;
        const version = recap.value?.version;
        if (!key || !recap.value || !version)
            return;
        const token = generation;
        const previous = { rating: recap.value.rating, reason: recap.value.reason };
        recap.value = { ...recap.value, rating: value, reason };
        rating.value = true;
        try {
            const saved = await orderRecapService.rate(key, version, value, reason);
            if (token === generation && recap.value?.version === version)
                recap.value = { ...recap.value, ...saved };
        }
        catch {
            if (token === generation && recap.value?.version === version)
                recap.value = { ...recap.value, ...previous };
            if (token === generation)
                fetchRecap(key, token);
        }
        finally {
            if (token === generation)
                rating.value = false;
        }
    }
    watch(orderKey, (key) => open(key), { immediate: true });
    onBeforeUnmount(() => {
        generation += 1;
        stopPolling();
    });
    return { recap, unavailable, slow, rating, refresh, rate };
}
